/**
 * CompactVqeFooter — the compact popover's footer: the standing "Open full
 * editor" CTA + the Done/Close button. `showOpenFull` is `false` while a
 * degrade card (top-level or nested) OR the DDL card owns the screen's single
 * primary CTA (§1.5 one-CTA) — `CompactVqeContent` computes that flag from its
 * own dispatch-chain state and passes it straight through; this component
 * renders the result only.
 *
 * CTA hierarchy (VQE Polish Advisor 2 §B2 item 3): `primaryCta` says which
 * action is the TRUE primary for the whole popover.
 *  - `'done'` (normal, editable state — `showOpenFull` is true): Done stays
 *    solid/primary; the footer's own ghost "Open full editor" renders too.
 *  - `'open-full'` (a degrade/DDL card owns the CTA — `showOpenFull` is
 *    false, so the footer's OWN "Open full editor" copy is hidden; the REAL
 *    primary lives in that card's own solid button): Done downgrades to
 *    ghost and relabels "Close" — there's nothing left to "confirm" when
 *    nothing in the popover is editable.
 *
 * Split out of `CompactVqeContent.tsx` (Plan 9 Task 4 line-budget pass) to
 * keep that module focused on the header + body dispatch chain.
 */

import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { Switch } from '../../../../../components/ui/switch';
import { VqeTooltip } from '../../render/common/VqeTooltip';
import { vqeCopy } from '../../ux/vqeCopy';

// TODO(vqe-copy): consolidate into vqeCopy.ts once the sole vqeCopy.ts writer
// for this batch (#4) has landed — see task-prim6a-degradedetail-brief.md.
const CLOSE_LABEL = 'Close';
const RUN_ON_CHANGE_LABEL = 'Run on change';
const RUN_ON_CHANGE_ON_TIP = 'Re-runs this SELECT after every edit here. Also under Settings → UI → Visual editor.';
const RUN_ON_CHANGE_OFF_TIP = 'Turn on to re-run this SELECT after every edit here. Also under Settings → UI → Visual editor.';

export type CompactVqeFooterPrimaryCta = 'open-full' | 'done';

export interface CompactVqeFooterProps {
  /** False while a degrade card (top-level or nested) or the DDL card already owns the primary CTA. */
  readonly showOpenFull: boolean;
  /** Which action is the true primary for the whole popover right now. */
  readonly primaryCta: CompactVqeFooterPrimaryCta;
  readonly onOpenFull: () => void;
  /** Close the popover (edits already committed to the buffer). */
  readonly onClose: () => void;
  /**
   * ANA-06 — truthful tooltip explaining WHY the standing "Open full editor" CTA
   * is offered when the statement isn't degraded but has a read-only-locked
   * window column (per-column degrade). `undefined` for the plain editable
   * state, so an ordinary SELECT's CTA stays label-only.
   */
  readonly openFullTooltip?: string;
  /** The "Edit applied · Undo" status, shown left of Done while visible. */
  readonly status?: ReactNode;
  /**
   * "Run on change" — a switch just left of Done (SELECT only; the caller
   * omits it for DML / DDL). It lives here rather than in the crowded header,
   * and stays put while the transient status appears to its left.
   */
  readonly runOnChange?: { readonly enabled: boolean; readonly onToggle: () => void };
}

export function CompactVqeFooter({
  showOpenFull,
  primaryCta,
  onOpenFull,
  onClose,
  openFullTooltip,
  status,
  runOnChange,
}: CompactVqeFooterProps) {
  const doneIsPrimary = primaryCta === 'done';
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
      {showOpenFull ? (
        <VqeTooltip content={openFullTooltip}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={onOpenFull}
            data-testid="vqe-compact-open-full"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {vqeCopy.actions.openFullEditor}
          </Button>
        </VqeTooltip>
      ) : null}
      <div className="ml-auto flex min-w-0 items-center gap-2">
        {status}
        {runOnChange ? (
          <VqeTooltip content={runOnChange.enabled ? RUN_ON_CHANGE_ON_TIP : RUN_ON_CHANGE_OFF_TIP}>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
              <Switch
                checked={runOnChange.enabled}
                onCheckedChange={() => runOnChange.onToggle()}
                aria-label="Run the query after every visual edit"
                data-testid="vqe-compact-run-on-change"
              />
              {RUN_ON_CHANGE_LABEL}
            </label>
          </VqeTooltip>
        ) : null}
        <Button
          type="button"
          variant={doneIsPrimary ? 'default' : 'ghost'}
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={onClose}
          data-testid="vqe-compact-apply"
        >
          {doneIsPrimary ? 'Done' : CLOSE_LABEL}
        </Button>
      </div>
    </div>
  );
}
